import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Briefcase } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DIPLO_IMG = "https://images.unsplash.com/photo-1774434834166-86066abd6b6c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } },
};

export default function DiplomaticService() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { lead_type: "diplomatic", ...formData });
      setSuccess(true);
    } catch (error) { setSubmitting(false); }
    setSubmitting(false);
  };

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  return (
    <section id="contact" data-testid="diplomatic-service" className="relative py-24 md:py-32 lg:py-40 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img src={DIPLO_IMG} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 z-[1] bg-black/75 backdrop-blur-[2px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left */}
          <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
            <motion.div variants={fadeUp} className="w-12 h-12 border border-[#C5A059]/30 flex items-center justify-center mb-6">
              <Briefcase size={20} className="text-[#C5A059]" strokeWidth={1.5} />
            </motion.div>
            <motion.p variants={fadeUp} className="font-sans text-[#C5A059] text-[10px] md:text-xs tracking-[0.25em] uppercase mb-4">
              Executive & Diplomatic
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-white text-4xl md:text-5xl font-light tracking-tight mb-6">
              Discreet.
              <br />
              <span className="italic">Impeccable.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="font-sans text-white/60 text-sm md:text-base font-light leading-relaxed mb-8">
              For diplomatic delegations, corporate executives, and high-profile guests requiring
              elevated security, privacy, and protocol. We partner with trusted local providers
              to deliver a seamless, world-class experience.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
              {["Armored Vehicles", "Security Detail", "Private Aviation", "Protocol Coordination", "Confidential Logistics"].map(tag => (
                <span key={tag} className="font-sans text-[10px] tracking-[0.15em] uppercase text-white/45 border border-white/10 px-4 py-2">
                  {tag}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* Right form */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3, duration: 0.8 }}>
            {success ? (
              <div className="bg-black/40 backdrop-blur-xl border border-white/10 p-12 text-center" data-testid="diplomatic-success">
                <h3 className="font-serif text-white text-2xl mb-3">Inquiry Received</h3>
                <p className="font-sans text-white/60 text-sm">A dedicated account manager will contact you within 12 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-black/40 backdrop-blur-xl border border-white/10 p-8 md:p-10 space-y-6"
                data-testid="diplomatic-form-fields">
                <input type="text" placeholder="Full Name / Organization" required value={formData.name}
                  onChange={e => update("name", e.target.value)}
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors placeholder:text-white/20"
                  data-testid="diplomatic-name-input" />
                <input type="email" placeholder="Email" required value={formData.email}
                  onChange={e => update("email", e.target.value)}
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors placeholder:text-white/20"
                  data-testid="diplomatic-email-input" />
                <input type="tel" placeholder="Phone" required value={formData.phone}
                  onChange={e => update("phone", e.target.value)}
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors placeholder:text-white/20"
                  data-testid="diplomatic-phone-input" />
                <textarea placeholder="Describe your requirements..." rows={4} required value={formData.message}
                  onChange={e => update("message", e.target.value)}
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors resize-none placeholder:text-white/20"
                  data-testid="diplomatic-message-input" />
                <button type="submit" disabled={submitting} data-testid="diplomatic-submit-btn"
                  className="w-full border border-[#C5A059] text-[#C5A059] font-sans text-xs tracking-[0.2em] uppercase py-4 hover:bg-[#C5A059] hover:text-black transition-all duration-500 disabled:opacity-30">
                  {submitting ? "Sending..." : "Request Consultation"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
